// Acquisition-channel capture for the public RFQ links (item 8, 2026-09-18).
//
// A link posted to a Facebook group carries ?utm_source=fb, but the visitor
// then walks to /login to register — and the query string is gone by the time
// the account is created. So the values are stashed on arrival and read back at
// signup, where they go into auth metadata and the set_profile_utm BEFORE
// INSERT trigger writes them onto the profile row.
//
// ⚠️ CHANGED 2026-11-01 (owner). This used to be the whole story: the values
// lived in sessionStorage only, by the owner's 2026-09-18 rule "under-
// attribution beats false attribution". That rule is retired. Sign-up now
// takes the FIRST-TOUCH source kept 30 days by src/lib/attribution.js (a
// direct visit never overwrites a known source); the sessionStorage copy below
// is only the fallback when that store is empty or blocked. Before changing
// it, the old rule was checked for what it protected: nothing reads
// profiles.utm_* (only set_profile_utm writes it) and 0 of 180 profiles had
// ever been tagged.
//
// Every read and write is wrapped: sessionStorage throws in some private modes
// and with site data blocked, and losing attribution must never break signup.

import { firstTouchUtm } from './attribution';

const KEY = 'maabar_utm';
const FIELDS = ['utm_source', 'utm_medium', 'utm_campaign'];
const MAX = 120; // matches left(v_val, 120) in set_profile_utm

// Capture from a query string. Returns what was stored, or null when the URL
// carried nothing — an untagged visit must NOT clear a tag from earlier in the
// same session (someone lands tagged, then navigates within the tab).
export function captureUtm(search) {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    return null;
  }

  const found = {};
  for (const f of FIELDS) {
    const raw = (params.get(f) || '').trim();
    if (raw) found[f] = raw.slice(0, MAX);
  }
  if (Object.keys(found).length === 0) return null;

  try {
    sessionStorage.setItem(KEY, JSON.stringify(found));
  } catch (storageErr) {
    // Attribution is worth less than the signup it rides on.
    console.error('[utmCapture] sessionStorage write failed:', storageErr?.message || storageErr);
  }
  return found;
}

// Read back at signup. Always an object, so callers can spread it unguarded.
export function readUtm() {
  const first = firstTouchUtm();
  if (Object.keys(first).length) return first;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out = {};
    for (const f of FIELDS) {
      if (typeof parsed[f] === 'string' && parsed[f].trim()) out[f] = parsed[f].slice(0, MAX);
    }
    return out;
  } catch (storageErr) {
    console.error('[utmCapture] sessionStorage read failed:', storageErr?.message || storageErr);
    return {};
  }
}
